import { useAuthContext } from '@authentication/Authentication';
import { useNotifications } from '@notifications/Notifications';
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { NetworkStatus, useNetworkStatus } from './useNetworkStatus';

export type WindowWithRegisterNetworkRecovery = Window & {
  __registerNetworkRecovery?: (callback: () => void) => () => void;
};

interface NetworkStatusContextType extends NetworkStatus {
  forceCheck: () => void;
  triggerRecovery: () => void;
}

const NetworkStatusContext = createContext<NetworkStatusContextType>({
  isOnline: true,
  isReconnecting: false,
  forceCheck: () => {},
  triggerRecovery: () => {},
});

export const NetworkStatusProvider = ({ children }: PropsWithChildren) => {
  const networkStatus = useNetworkStatus();
  const { addNotification } = useNotifications();
  const { authenticated } = useAuthContext();

  const prevOnlineRef = useRef(networkStatus.isOnline);
  const hasShownOfflineNotification = useRef(false);
  const hasShownOnlineNotification = useRef(false);

  // Recovery callbacks that other components can register
  const recoveryCallbacks = useRef<(() => void)[]>([]);

  const triggerRecovery = useCallback(() => {
    recoveryCallbacks.current.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('NetworkStatusProvider: Recovery callback failed:', error);
      }
    });
  }, []);

  // Register recovery callback (used by Apollo and Auth providers)
  const registerRecoveryCallback = useCallback((callback: () => void) => {
    recoveryCallbacks.current.push(callback);
    return () => {
      recoveryCallbacks.current = recoveryCallbacks.current.filter((cb) => cb !== callback);
    };
  }, []);

  // Handle network status changes
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    const isOnlineNow = networkStatus.isOnline;

    if (wasOnline !== isOnlineNow) {
      if (!isOnlineNow) {
        // Just went offline
        if (!hasShownOfflineNotification.current) {
          addNotification({
            text: 'Connection lost. Attempting to reconnect...',
            type: 'warning',
            duration: 5000,
          });
          hasShownOfflineNotification.current = true;
          hasShownOnlineNotification.current = false;
        }
      } else if (!hasShownOnlineNotification.current) {
        // Just came back online
        addNotification({
          text: 'Connection restored. Refreshing data...',
          type: 'success',
          duration: 3000,
        });
        hasShownOnlineNotification.current = true;
        hasShownOfflineNotification.current = false;

        // Only trigger recovery if user is authenticated
        if (authenticated) {
          // Small delay to ensure network is stable
          setTimeout(() => {
            triggerRecovery();
          }, 500);
        }
      }

      prevOnlineRef.current = isOnlineNow;
    }
  }, [networkStatus.isOnline, addNotification, authenticated, triggerRecovery]);

  // Reset notification flags when reconnecting state changes
  useEffect(() => {
    if (!networkStatus.isReconnecting) {
      // Reset flags when we're not in a reconnecting state
      const timeout = setTimeout(() => {
        hasShownOfflineNotification.current = false;
      }, 5000); // Reset after 5 seconds of stable connection

      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [networkStatus.isReconnecting]);

  // Expose registration function globally for other providers
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (
        window as { __registerNetworkRecovery?: (callback: () => void) => () => void }
      ).__registerNetworkRecovery = registerRecoveryCallback;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as { __registerNetworkRecovery?: (callback: () => void) => () => void })
          .__registerNetworkRecovery;
      }
    };
  }, [registerRecoveryCallback]);

  const contextValue: NetworkStatusContextType = useMemo(() => {
    return {
      ...networkStatus,
      triggerRecovery,
    };
  }, [networkStatus, triggerRecovery]);

  return (
    <NetworkStatusContext.Provider value={contextValue}>{children}</NetworkStatusContext.Provider>
  );
};
